import { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Radio, WifiOff, Loader2 } from "lucide-react";
import { fmtPct, fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { Candles } from "@/components/Candles";

const INTERVALS = [1, 5, 15, 60];
const fmtInterval = (s) => (s >= 60 ? `${s / 60}m` : `${s}s`);

const fmtTime = (t) => new Date(t).toLocaleTimeString([], { hour12: false, minute: "2-digit", second: "2-digit" });

const StatusBadge = ({ status, source, feed }) => {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-ticker uppercase tracking-widest text-emerald-300" data-testid="live-status-live">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        Live · EV-IDEX feed{source === "relay" ? " (relay)" : ""}
      </span>
    );
  }
  if (status === "connecting") {
    return <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400" data-testid="live-status-connecting"><Loader2 className="w-3 h-3 animate-spin" /> Connecting…</span>;
  }
  if (status === "quiet") {
    return <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-ticker uppercase tracking-widest text-yellow-200" data-testid="live-status-quiet"><Radio className="w-3 h-3" /> Quiet · feed connected, no recent trade</span>;
  }
  return <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-ticker uppercase tracking-widest text-amber-300" data-testid={`live-status-${status}`}><WifiOff className="w-3 h-3" /> {status === "unavailable" ? "No live feed for this asset" : feed === "connected" ? "Stale · no trade for over 5 min" : "Disconnected"}</span>;
};

export const LiveChart = ({ live, symbol, mode, onMode, interval, onInterval, showMA = false, showBB = false }) => {
  const { status, feed, source, price, bid, ask, open24h, series, candles, ticks } = live;
  const [flash, setFlash] = useState(null);
  const prevPrice = useRef(null);
  const [rate, setRate] = useState(0);
  const tickWindow = useRef([]);

  useEffect(() => {
    if (price == null) return;
    if (prevPrice.current != null && price !== prevPrice.current) {
      setFlash(price > prevPrice.current ? "up" : "down");
      const id = setTimeout(() => setFlash(null), 450);
      prevPrice.current = price;
      return () => clearTimeout(id);
    }
    prevPrice.current = price;
  }, [price]);

  useEffect(() => {
    if (tickWindow.current.length && ticks < tickWindow.current[tickWindow.current.length - 1].n) tickWindow.current = [];
    tickWindow.current = [...tickWindow.current, { t: Date.now(), n: ticks }].filter((x) => Date.now() - x.t <= 10000);
    const w = tickWindow.current;
    if (w.length > 1) setRate(((w[w.length - 1].n - w[0].n) / ((w[w.length - 1].t - w[0].t) / 1000)).toFixed(1));
  }, [ticks]);

  const first = series[0]?.p;
  const sessionChange = first && price ? ((price - first) / first) * 100 : null;
  const change24 = open24h && price ? ((price - open24h) / open24h) * 100 : null;
  const up = (sessionChange ?? 0) >= 0;
  const color = up ? "#10B981" : "#EF4444";
  const spread = bid && ask ? ask - bid : null;
  const lo = series.length ? Math.min(...series.map((d) => d.p)) : null;
  const hi = series.length ? Math.max(...series.map((d) => d.p)) : null;
  const pad = lo != null && hi != null ? Math.max((hi - lo) * 0.25, hi * 0.00005) : 0;

  return (
    <div data-testid="live-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Hint title="Live market rate" text="Real-time trades streamed from the EV-IDEX market feed and sampled once per second. Works independently of CoinGecko." asChild>
              <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 cursor-help inline-flex items-center gap-1"><Radio className="w-3 h-3 text-cyan-300" /> {symbol?.toUpperCase()}/USD · 1s</span>
            </Hint>
            <StatusBadge status={status} source={source} feed={feed} />
          </div>
          <div
            data-testid="live-price"
            className={`font-mono-ticker text-2xl md:text-3xl font-bold mt-1 transition-colors duration-300 ${flash === "up" ? "text-emerald-300" : flash === "down" ? "text-red-300" : "text-slate-100"}`}
          >
            {price != null ? fmtUsd(price, { digits: price < 1 ? 6 : price < 100 ? 4 : 2 }) : "—"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 max-w-full min-w-0">
        <div className="flex gap-2 text-[11px] font-mono-ticker max-w-full overflow-x-auto" data-testid="live-mode-row">
          <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
            {["candles", "line"].map((m) => (
              <Hint key={m} title={m === "candles" ? "Candlesticks" : "Line"} text={m === "candles" ? "Each candle shows open, high, low and close for the interval; green closes up, red closes down. The last candle moves with every trade." : "Last traded price sampled once per second."} asChild>
              <button data-testid={`live-mode-${m}`} onClick={() => onMode(m)}
                className={`px-2 py-0.5 rounded-md capitalize ${mode === m ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}>{m}</button>
              </Hint>
            ))}
          </div>
          {mode === "candles" && (
            <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800" data-testid="live-interval-picker">
              {INTERVALS.map((iv) => (
                <Hint key={iv} title={`${fmtInterval(iv)} candles`} text={`Each candle aggregates ${fmtInterval(iv)} of trades.`} asChild>
                <button data-testid={`live-interval-${iv}`} onClick={() => onInterval(iv)}
                  className={`px-2 py-0.5 rounded-md ${interval === iv ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-slate-200"}`}>{fmtInterval(iv)}</button>
                </Hint>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-1 text-right">
          <Mini label="Session" value={sessionChange != null ? fmtPct(sessionChange, 3) : "—"} c={sessionChange >= 0 ? "text-emerald-400" : "text-red-400"} tid="live-session-change" />
          <Mini label="24h" value={change24 != null ? fmtPct(change24) : "—"} c={change24 >= 0 ? "text-emerald-400" : "text-red-400"} />
          <Mini label="Spread" value={spread != null ? fmtUsd(spread, { digits: spread < 0.01 ? 6 : 2 }) : "—"} />
          <Mini label="Ticks/s" value={status === "live" ? rate : "—"} tid="live-tick-rate" />
        </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 320 }} className="relative">
        {(mode === "candles" ? candles.length < 1 : series.length < 2) ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500" data-testid="live-waiting">
            {status === "unavailable" ? "This asset has no supported real-time market pair, so there is no per-second feed. Use the 24H range instead." : "Waiting for the first ticks…"}
          </div>
        ) : mode === "candles" ? (
          <Candles data={candles} timeFormatter={fmtTime} showVolume refLine={first} showMA={showMA} showBB={showBB} testid="live-candles" />
        ) : (
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="liveG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={color} stopOpacity={0.45} />
                  <stop offset="1" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="t" tickFormatter={fmtTime} stroke="#64748b" fontSize={10} tickLine={false} minTickGap={60} />
              <YAxis domain={[lo - pad, hi + pad]} stroke="#64748b" fontSize={10} width={80} tickFormatter={(v) => fmtUsd(v, { digits: v < 1 ? 6 : 2 })} />
              <Tooltip
                contentStyle={{ background: "#0F141C", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }}
                labelFormatter={fmtTime}
                formatter={(v) => [fmtUsd(v, { digits: v < 1 ? 6 : 2 }), "Price"]}
              />
              {first && <ReferenceLine y={first} stroke="#64748b" strokeDasharray="4 4" />}
              <Area type="monotone" dataKey="p" stroke={color} strokeWidth={2} fill="url(#liveG)" isAnimationActive={false} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-2 font-mono-ticker">
        <span data-testid="live-points">{mode === "candles" ? `${candles.length} candles · ${fmtInterval(interval)} · ${candles[candles.length - 1]?.n ?? 0} trades in current` : `${series.length} samples · last ${Math.min(series.length, 300)}s`}</span>
        <span>Bid {bid ? fmtUsd(bid, { digits: bid < 1 ? 6 : 2 }) : "—"} · Ask {ask ? fmtUsd(ask, { digits: ask < 1 ? 6 : 2 }) : "—"}</span>
      </div>
    </div>
  );
};

const Mini = ({ label, value, c = "text-slate-200", tid }) => (
  <div>
    <div className="text-[9px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-mono-ticker text-xs font-semibold ${c}`} data-testid={tid}>{value}</div>
  </div>
);
